<template>
  <GcBaseLayout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :backButtonEnable="!!backButtonRoute"
    :backButtonFunction="backButtonFunction"
    :formsConfig="formsConfig"
    :tabs="[]"
    :selectedIndex="undefined"
    ref="gc-base-layout"
    class="journal-v2"
  >
    <slot></slot>
    <template #headerContent>
      <GcHeaderContentDynamic
        :title="getScreenJson.title"
        :createModeIsEditMode="true"
        :baseView="baseView"
        :primaryLabel="
          baseView.mode === 'create'
            ? 'Create Journal'
            : 'Journal # ' + baseView.id
        "
        :secondaryLabel="undefined"
        v-if="loaded"
      />
    </template>
    <div class="journal-screen-container">
      <div id="journal-screen">
        <div v-if="loaded" class="css-v2 transaction-screen">
          <div class="gc-header-toolbar">
            <gc-form-header-toolbar
              :toolbar-options="getHeaderToolbar"
              :data-model="loaded ? model : {}"
              :itemSaveCallBack="saveJournal"
              :backRoute="backButtonRoute"
              :setLoading="showLoading"
              ref="gc-form-header-toolbar"
              :baseView="baseView"
            ></gc-form-header-toolbar>
          </div>

          <div class="gc-header-form" v-show="!gridExpand">
            <div class="header-form-container">
              <div class="header-form">
                <div class="view-card responsive-paddings">
                  <gc-form
                    :formOptions="{
                      gcLabelLocation: 'left',
                    }"
                    :formItems="getHeaderFormItems"
                    :isNormal="true"
                    :data-model="loaded ? model : {}"
                    :formMode="baseView.mode"
                    :fromFrom="'gc-header-form'"
                    ref="gc-header-form"
                    :checkValid="isValid"
                    :validationGroup="'gc-header-form'"
                    :scrollingEnabled="false"
                    :formContentReady="formContentReady"
                    :formHeight="100"
                  ></gc-form>
                </div>
              </div>
            </div>
          </div>
          <div class="gc-data-grid-so-v5">
            <dx-data-grid
              :data-source="dataSourceJournalEntry"
              :columns="getColumns"
              :height="getGridHeight - 20"
              :column-chooser="{ enabled: false }"
              :ref="'dx-data-grid-comp'"
              :key-expr="'ID'"
              :repaint-changes-only="true"
              :show-borders="true"
              :two-way-binding-enabled="false"
              :focusedRowEnabled="true"
              v-if="loaded"
              @toolbar-preparing="onToolbarPreparing($event)"
              @editor-preparing="onEditorPreparing"
              @cell-prepared="onCellPrepared"
              @row-prepared="onRowPrepared"
              :on-row-removed="onRowRemoved"
            >
              <DxScrolling
                :mode="'virtual'"
                :row-rendering-mode="'standard'"
                :preload-enabled="true"
              />
              <DxEditing
                :allow-deleting="baseView.mode !== 'review'"
                :allow-updating="baseView.mode !== 'review'"
                :mode="'cell'"
                :changes="changes"
              />

              <DxKeyboardNavigation
                :edit-on-key-press="true"
                :enter-key-action="'startEdit'"
                :enter-key-direction="'none'"
                :enabled="true"
              />

              <DxPaging :page-size="200" :enabled="false" />

              <DxPager
                :showInfo="true"
                :showPageSizeSelector="false"
                :infoText="'({2} items)'"
                :visible="false"
                class="gc-pager"
                cssClass="gc-pages-2"
              />

              <template #Decimal="{ data }">
                <GcDecimal :dataValue="data" :format="data.column.format" />
              </template>
              <template #DecimalCell="{ data }">
                <GcDecimalCell :dataValue="data" :format="data.column.format" />
              </template>
            </dx-data-grid>
          </div>

          <GcFooter v-if="showFooter && loaded">
            <template #rightContent>
              <div
                class="gc-grid-footer-item"
                v-for="item in footerRightItems"
                :key="item.label"
              >
                <label class="gc-footer-label">{{ item.label }}</label>
                <GcFooterItem
                  :value="item.value"
                  :format="item.format"
                  :length="item.format === 'Decimal-16-4C' ? 8 : 6"
                />
              </div>
            </template>
          </GcFooter>
        </div>
      </div>
    </div>

    <gcPopupUploader
      :popupVisible.sync="visibleFileUploader"
      :returnImportFile="returnImportFile"
    />
  </GcBaseLayout>
</template>
<script>
import FooterItemConfig from "@/_helpers/data-grid/footer-items";
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import DataGridMixin from "@/_mixins/data-grid.mixin";
import ArrayStore from "devextreme/data/array_store";
import { mapGetters } from "vuex";
import { getFactorByCode } from "@/_store/helpers";
import { JournalDetailServices } from "@/_services/api/finance/journalDetail";
import { DefaultCompanyBranchDivisionService } from "@/_services/api/sales/default-company-branch-dicision.service";
import { DivisionService } from "@/_services/api/sales/division.service";
import { BranchService } from "@/_services/api/sales/branch.service";
import { ProductService } from "@/_services/api/products/product.service";
import {
  backwardTabbing,
  forwardTabbing,
  onEnter,
} from "@/_helpers/ui/keyboard";
import ColumnWidth from "@/_helpers/data-grid/ColumnWidth";
import { FeatureStorage } from "../../_services/storage";
import gcPopupUploader from "../../popups/gc-popup-uploader.vue";

const { getFooterRightItems } = FooterItemConfig;
let journalBase = {
  ID: 1,
  GLChartCode: "",
  ComputedStringCode: "",
  GLChartDescription: "",
  BranchCode: "",
  BranchDescription: "",
  DivisionCode: "",
  DivisionDescription: "",
  Comment: "",
  Amount: 0,
  IsNewLine: false,
  IsValid: true,
};

function countLineBreaks(text) {
  return (text.match(/\n/g) || []).length;
}

export default {
  mixins: [RouteMixin, FormMixin, DataGridMixin],
  components: {
    gcPopupUploader,
  },
  data() {
    return {
      isFirstLoad: true,
      ui: {
        DateAdjustedMin: new Date(),
        DateAdjustedMax: new Date(),
      },
      model: {
        Status: "D",
        FriendlyStatus: undefined,
        DateIn: new Date(),
        Reversed: false,
        Narration: null,
        isDefaultComment: false,
        AccountingPeriod: undefined,
        MinDate: undefined,
        MaxDate: undefined,
        CompanyCode: undefined,
        DateCreated: null,
        CreatedBy: null,
      },
      isValid: false,
      loaded: false,
      showFooter: false,
      dataSourceJournalEntry: new ArrayStore({
        key: "ID",
        data: [],
      }),
      footerItems: [],
      headerFormHeight: 0,
      validationColumn: undefined,
      validationDataRowKey: undefined,
      defaultDivisionCode: null,
      defaultBranchCode: null,
      changes: [],
      selectedAccount: undefined,
      selectedBranch: undefined,
      selectedDivision: undefined,
      onTab: false,
      lastTabID: null,
      branchDataSourceItems: [],
      divisionDataSourceItems: [],
      ellipsisMenu: [],
      visibleFileUploader: false,
      glaccountsDataItems: [],
    };
  },
  computed: {
    ...mapGetters({ gridHeight: "ui/getHeightForDataGrid" }),
    ...mapGetters({ gridExpand: "ui/getGridExpand" }),
    ...mapGetters({ reviewDataSource: "dataContext/getReviewDataSource" }),
    ...mapGetters({ dataViewType: "dataContext/getDataViewType" }),
    ...mapGetters({
      getValidationSummaryVisible: "ui/getValidationSummaryVisible",
    }),
    getScreenJson() {
      return {
        title: "Journal",
      };
    },
    footerRightItems() {
      return getFooterRightItems(this.footerItems, this.model);
    },
    getHeaderToolbar() {
      return {
        audit: this.baseView.mode === "review",
        status: true,
        save:
          this.model.Status !== "C" &&
          this.model.Status !== "X" &&
          this.baseView.mode !== "review",
        // saveOptions: {
        //   items: this.baseView.id
        //     ? [
        //         { text: "Save Pending", id: "P" },
        //         { text: "Save & Commit", id: "C", default: true },
        //         { text: "Cancel", id: "X" },
        //       ]
        //     : [
        //         { text: "Save Pending", id: "P" },
        //         { text: "Save & Commit", id: "C", default: true },
        //       ],
        //   keyExpr: "id",
        //   displayExpr: "text",
        //   dropDownOptions: {
        //     width: "150px",
        //   },
        //   saveKey: "NextStatus",
        // },

        // saveCallback: this.saveStockTransfer,
        screenName: "Stock Transfers - All",
        pdf: this.baseView.mode === "review",

        toolbarFormItems: [
          // {
          //   dataField: "DateIn",
          //   editorType: "dxDateBox",
          //   label: {
          //     text: "Date",
          //   },
          //   editorOptions: {
          //     displayFormat: "dd MMM yyyy",
          //     width: 120,
          //     min: this.minDate,
          //     max: this.ui.DateAdjustedMax,
          //     readOnly: this.baseView.mode === "review",
          //     tabIndex: 1,
          //     onFocusIn: (e) => {
          //       e.event.target.select();
          //     },
          //   },
          // validationRules: [
          //   {
          //     type: "required",
          //     message: "Date Received is required",
          //   },
          // ],
          // },
          // },
        ],
        auditItems: [
          {
            itemType: "group",
            caption: "",
            items: [
              {
                dataField: "FormattedDateCreated",
                label: { text: "Created By", location: "left" },
                editorType: "dxTextBox",
                editorOptions: {
                  // // disabled: true,
                },
              },
              {
                dataField: "FormattedDateChanged",
                label: { text: "Changed By", location: "left" },
                editorType: "dxTextBox",
                editorOptions: {
                  // disabled: true,
                },
              },
            ],
          },
        ],
        ellipsis: {
          visible: this.baseView.mode === "review",
          menuItems: this.ellipsisMenu,
          clickAction: (e) => {
            if (e.itemData.menuText === "Copy") {
              this.onCopyClickHandle();
            }
          },
        },
      };
    },
    getHeaderFormItems() {
      return [
        {
          itemType: "group",
          caption: "",
          colCount: 2,
          items: [
            {
              itemType: "group",
              caption: "",
              items: [
                {
                  screenField: "DateIn",
                  editorOptions: {
                    tabIndex: 1,
                    min: this.model.MinDate,
                    max: this.model.MaxDate,
                    allowUpdate: this.baseView.mode !== "review",
                    onKeyDown: (e) => {
                      if (backwardTabbing(e)) {
                        e.event.preventDefault();
                      }
                    },
                  },
                  required: true,
                },
                {
                  dataField: "Reversed",
                  label: {
                    text: "Auto Reverse",
                  },
                  editorType: "dxCheckBox",
                  editorOptions: {
                    tabIndex: 2,
                    // onValueChanged: () => {
                    //   this.onChangeFXFreight();
                    // },
                  },
                },
                {
                  dataField: "isDefaultComment",
                  label: {
                    text: "Default Comment",
                  },
                  visible: this.baseView.mode !== "review",
                  editorType: "dxCheckBox",
                  editorOptions: {
                    tabIndex: 3,
                    onValueChanged: (e) => {
                      if (e.value) {
                        this.setAllEmptyCommentsItem();
                      }
                    },
                  },
                },
              ],
            },
            {
              dataField: "Narration",
              label: { text: " " },
              editorType: "dxTextArea",
              editorOptions: {
                placeholder:
                  this.baseView.mode === "review" ? "" : "Enter narration here",
                tabIndex: 4,
                maxLength: 100,
                height: 100,
                onFocusOut: () => {
                  if (this.model.isDefaultComment) {
                    this.setAllEmptyCommentsItem();
                  }
                },
                onKeyDown: (e) => {
                  if (forwardTabbing(e)) {
                    e.event.preventDefault();
                    this.focusOnEditableCell(0);
                  }
                },
              },
            },
          ],
        },
      ];
    },
    getColumns() {
      return [
        {
          screenField: "GLChart",
          dataField: "GLChartCode",
          dataType: "string",
          caption: "Account",
          alignment: "left",
          allowUpdate: true,
          allowSearch: true,
          minWidth: ColumnWidth.AccountCode.minWidth,
          width: ColumnWidth.AccountCode.width,
          // cssClass: "edit-cell-textbox-field",
          editorOptions: {
            // onOpened: (e) => {
            //   if (e) {
            //     e.component.getDataSource()._searchValue = '';
            //     e.component.getDataSource().reload();
            //   }
            // },
            dataSource: ProductService.dataSourceConfig("api/GLChart", {
              filter: [["Inactive", "=", "N"], "and", ["Status", "=", "A"]],
              select: ["ComputedStringCode", "Description"],
              sort: "ComputedStringCode asc",
            }),
            searchExpr: ["ComputedStringCode", "Description"],
            searchTimeout: 10,
            searchEnabled: true,
            // showDataBeforeSearch: false,
            // minSearchLength: 1,
            displayExpr: "ComputedStringCode",
            valueExpr: "ComputedStringCode",
            valueChangeEvent: "focusout",
            onFocusOut: async (e, lineData) => {
              if (
                this.onTab &&
                (lineData.data.GLChartCode === null ||
                  lineData.data.GLChartCode === "" ||
                  typeof lineData.data.GLChartCode === "undefined")
              ) {
                this.setTab(false);
                if (this.lastTabID === lineData.data.ID) {
                  await this.showGlCodeAlert(lineData.rowIndex);
                }
              }
            },
            onFocusIn: (e) => {
              e.event.target.select();
            },
            itemTemplate: (data) => {
              return ` <div class="custom-auto-complete">
                        <span class="auto-complete-primary">
                          ${data.Description}
                        </span>
                        <span class="record-secondary gc-grid-code">
                          ${data.ComputedStringCode}
                        </span>
                      </div>`;
            },
            dropDownOptions: {
              width: 200,
            },

            onKeyDown: (e, lineData) => {
              if (forwardTabbing(e) || onEnter(e)) {
                this.setTab();
                this.setLastTabID(lineData.data.ID);
              }
              if (backwardTabbing(e)) {
                if (lineData.rowIndex === 0) {
                  e.event.preventDefault();
                  this.goToCommentForm();
                }
              }
            },
            // onItemClick: (e, lineData) => {
            //   // this.onEnterProductCode({ event: { key: "Enter" } }, lineData);
            //
            //   this.dataGrid.byKey(lineData.data.ID).then((res) => {
            //     if (res.Onhand < 0 || res.IsBatchProduct !== "No")
            //       this.focusOnCellByRowIndex(lineData.data, "Comment");
            //     else this.focusOnCellByRowIndex(lineData.data, "TransferQty");
            //   });
            // },
            onSelectionChanged: (e) => {
              this.setSelectedAccount(e);
            },
          },
          setCellValue: (newData, value, currentRowData) => {
            if (this.selectedAccount !== null) {
              newData.GLChartCode = value;
              newData.GLChartDescription = this.selectedAccount.Description;
              newData.ComputedStringCode = value;
            }
            newData.IsNewLine = true;
            this.updateLocalDataSet("update", newData);
            if (
              !currentRowData.GLChartCode &&
              currentRowData.IsNewLine !== true
            ) {
              this.addNewRow(false);
              const index = this.dataGrid.getRowIndexByKey(currentRowData.ID);
              setTimeout(
                () => this.focusOnEditableCell(index, "BranchCode"),
                10
              );
            }
          },
        },
        {
          dataField: "ComputedStringCode",
          minWidth: ColumnWidth.ProductDescription.minWidth,
          width: ColumnWidth.ProductDescription.width,
          dataType: "string",
          allowEditing: false,
          showEditorAlways: false,
          allowSearch: true,
          allowSorting: false,
          visible: false,
        },
        {
          screenField: "Description",
          caption: "Description",
          dataField: "GLChartDescription",
          minWidth: ColumnWidth.ProductDescription.minWidth,
          width: ColumnWidth.ProductDescription.width,
          allowEditing: false,
          showEditorAlways: false,
          allowSearch: true,
          allowSorting: false,
        },
        {
          screenField: "Branch",
          caption: "Branch",
          dataField: "BranchCode",
          gridDataField: undefined,
          allowUpdate: true,
          allowSearch: true,
          width: ColumnWidth.BranchCode.width,
          maxWidth: ColumnWidth.BranchCode.width,
          cssClass: "edit-cell-textbox-field",
          lookup:
            this.baseView.mode !== "review"
              ? {
                  allowClearing: false,
                  dataSource: this.branchDataSourceItems,
                  displayExpr: "Code",
                  valueExpr: "Code",
                }
              : undefined,
          editorOptions: {
            // onOpened: (e) => {
            //   if (e) {
            //     e.component.getDataSource()._searchValue = '';
            //     e.component.getDataSource().reload();
            //   }
            // },
            // dataSource: BranchService.dataSourceConfig("api/Branch", {
            //   filter: [["Inactive", "=", "N"]],
            //   select: ["Code", "Description"],
            //   sort: "Code asc",
            // }),
            searchExpr: ["Code", "Description"],
            searchTimeout: 10,
            searchEnabled: true,
            // showDataBeforeSearch: false,
            // minSearchLength: 1,
            displayExpr: "Code",
            valueExpr: "Code",
            valueChangeEvent: "focusout",
            onFocusOut: async (e, lineData) => {
              if (
                lineData.data.BranchCode === null ||
                lineData.data.BranchCode === "" ||
                typeof lineData.data.BranchCode === "undefined"
              ) {
                await this.$alert(
                  `The branch provided is not a valid branch for the journal. Please select a valid branch.`,
                  "Warning"
                );
                this.focusOnEditableCell(lineData.rowIndex, "BranchCode");
              }
            },
            onFocusIn: (e) => {
              e.event.target.select();
            },
            itemTemplate: (data) => {
              return ` <div class="custom-auto-complete">
                        <span class="auto-complete-primary">
                          ${data.Description}
                        </span>
                        <span class="record-secondary gc-grid-code">
                          ${data.Code}
                        </span>
                      </div>`;
            },
            dropDownOptions: {
              width: 300,
            },
            // onKeyDown: this.onEnterProductCode,
            // onItemClick: (e, lineData) => {
            //   // this.onEnterProductCode({ event: { key: "Enter" } }, lineData);
            //
            //   this.dataGrid.byKey(lineData.data.ID).then((res) => {
            //     if (res.Onhand < 0 || res.IsBatchProduct !== "No")
            //       this.focusOnCellByRowIndex(lineData.data, "Comment");
            //     else this.focusOnCellByRowIndex(lineData.data, "TransferQty");
            //   });
            // },
            onSelectionChanged: (e) => {
              this.setSelectedBranch(e);
            },
          },
        },
        {
          screenField: "Division",
          caption: "Division",
          dataField: "DivisionCode",
          gridDataField: undefined,
          allowUpdate: true,
          allowSearch: true,
          width: ColumnWidth.BranchCode.width,
          maxWidth: ColumnWidth.BranchCode.width,
          cssClass: "edit-cell-textbox-field",
          lookup:
            this.baseView.mode !== "review"
              ? {
                  allowClearing: false,
                  dataSource: this.divisionDataSourceItems,
                  displayExpr: "Code",
                  valueExpr: "Code",
                }
              : undefined,
          editorOptions: {
            // onOpened: (e) => {
            //   if (e) {
            //     e.component.getDataSource()._searchValue = '';
            //     e.component.getDataSource().reload();
            //   }
            // },
            // dataSource: DivisionService.dataSourceConfig("api/Division", {
            //   filter: [["Inactive", "=", "N"]],
            //   select: ["Code", "Description"],
            //   sort: "Code asc",
            // }),
            searchExpr: ["Code", "Description"],
            searchTimeout: 10,
            searchEnabled: true,
            // showDataBeforeSearch: false,
            // minSearchLength: 1,
            displayExpr: "Code",
            valueExpr: "Code",
            valueChangeEvent: "focusout",
            onFocusIn: (e) => {
              e.event.target.select();
            },
            itemTemplate: (data) => {
              return ` <div class="custom-auto-complete">
                        <span class="auto-complete-primary">
                          ${data.Description}
                        </span>
                        <span class="record-secondary gc-grid-code">
                          ${data.Code}
                        </span>
                      </div>`;
            },
            dropDownOptions: {
              width: 200,
            },
            // onKeyDown: this.onEnterProductCode,
            // onItemClick: (e, lineData) => {
            //   // this.onEnterProductCode({ event: { key: "Enter" } }, lineData);
            //
            //   this.dataGrid.byKey(lineData.data.ID).then((res) => {
            //     if (res.Onhand < 0 || res.IsBatchProduct !== "No")
            //       this.focusOnCellByRowIndex(lineData.data, "Comment");
            //     else this.focusOnCellByRowIndex(lineData.data, "TransferQty");
            //   });
            // },
            onSelectionChanged: (e) => {
              this.setSelectedDivision(e);
            },
            onFocusOut: async (e, lineData) => {
              if (
                lineData.data.DivisionCode === null ||
                lineData.data.DivisionCode === "" ||
                typeof lineData.data.DivisionCode === "undefined"
              ) {
                await this.$alert(
                  `The division provided is not a valid division for the journal. Please select a valid division.`,
                  "Warning"
                );
                this.focusOnEditableCell(lineData.rowIndex, "DivisionCode");
              }
            },
          },
        },
        {
          caption: "Comments",
          dataField: "Comment",
          // allowEditing: this.baseView.mode != "review",
          // showEditorAlways: true,
          allowSearch: this.baseView.mode === "review",
          allowSorting: this.baseView.mode === "review",
          allowEditing:
            this.baseView.mode !== "review" &&
            this.dataGridViewType !== "review",
          cssClass: "edit-cell-textbox-field comment-text",
          editorOptions: {
            onFocusIn: (e) => {
              e.event.target.select();
            },
            maxLength: 100,
            maxHeight: 103,
            height: 30,
            valueChangeEvent: "input",
            onInput: (e) => {
              this.commentFieldHeightUpdate(e);
            },
            onInitialized: (e) => {
              this.commentFieldHeightUpdate(e);
            },
          },
          // minWidth: ColumnWidth.Comment.minWidth,
          // width: ColumnWidth.Comment.width,
          customizeText: (e) => {
            if (e.value) return e.value.replaceAll("\n", "<br/>\r\n");
            else return "";
          },
          encodeHtml: false,
          cellTemplate: (data, info) => {
            return (data.innerHTML = `<div class='comment-cell-template'>${info.text}</div>`);
          },
        },
        {
          dataField: "AmountString",
          dataType: "string",
          allowEditing: false,
          showEditorAlways: false,
          allowSearch: true,
          allowSorting: false,
          visible: false,
        },
        {
          screenField: "FXPrice",
          dataField: "Amount",
          label: "Amount",
          minWidth: ColumnWidth.Amount.minWidth,
          width: ColumnWidth.Amount.width,
          cellTemplate: "Decimal",
          cssClass: "edit-cell-textbox-field",
          dataType: "number",
          format: "##,###,###,###,##0.00",
          showEditorAlways: this.baseView.mode !== "review",
          allowFiltering: false,
          allowSearch: true,
          allowSorting: false,
          editorOptions: {
            valueChangeEvent: "focusout",
            format: "###,###,##0.00",
            max: 999999999.0,
            // min: 0,
            step: 0,
            onFocusIn: (e) => {
              e.event.target.select();
            },
            // onInput: (e) => {
            //   const inputElement = e.event.target;
            //   if (inputElement.value.length > 16)
            //     inputElement.value = inputElement.value.slice(0, 16);
            // },

            onKeyDown: async (e, lineData) => {
              if (forwardTabbing(e)) {
                if (
                  lineData.data.GLChartCode === null ||
                  lineData.data.GLChartCode === "" ||
                  typeof lineData.data.GLChartCode === "undefined"
                ) {
                  e.event.preventDefault();
                  this.setTab(false);
                  await this.showGlCodeAlert(lineData.rowIndex);
                }
                // else if(lineData.data.Amount === 0){
                //   const rowIndex = this.dataGrid.getRowIndexByKey(lineData.data.ID);
                //   await this.$alert(`The amount provided is not a valid amount for the journal. Please fill in a valid amount.`, 'Warning');
                //   setTimeout(() => this.focusOnEditableCell(rowIndex, "Amount"), 150)
                //   return false;
                // }
                else {
                  this.setTab(true);
                  const currentData =
                    this.dataSourceJournalEntry._array[
                      this.dataSourceJournalEntry._array.length - 1
                    ];
                  if (currentData.ID === lineData.data.ID) {
                    setTimeout(() => this.addNewRow(true), 1);
                  }
                }
              }
            },
          },
          setCellValue: async (newData, value, currentRowData) => {
            // if(value === 0){
            //   newData.Amount = currentRowData.Amount;
            //   await this.$alert(`The amount provided is not a valid amount for the journal. Please fill in a valid amount.`, 'Warning');
            //   const rowIndex = this.dataGrid.getRowIndexByKey(currentRowData.ID);
            //   setTimeout(() => this.dataGrid.repaintRows([rowIndex]), 50);
            //   setTimeout(() => this.focusOnEditableCell(rowIndex, "Amount"), 75)
            //   return false;
            // }

            newData.Amount = value;
            newData.AmountString = value;
            newData.IsValid = true;

            const rowIndex = this.dataGrid.getRowIndexByKey(currentRowData.ID);
            setTimeout(() => this.dataGrid.repaintRows([rowIndex]), 50);

            if (this.onTab) {
              this.setTab(false);
              const nextIndex = this.getNextIndex(
                this.dataGrid,
                this.dataSourceJournalEntry._array,
                "ID",
                currentRowData.ID
              );
              if (nextIndex !== -1) {
                setTimeout(() => this.focusOnEditableCell(nextIndex), 75);
              }
            }
          },
        },
        {
          caption: "",
          dataField: "",
          type: "buttons",
          width: this.baseView.mode === "review" ? 10 : 100,
          minWidth: 10,
          buttons: [
            {
              name: "add",
              icon: "add",
              visible: this.baseView.mode !== "review",
              onClick: (e) => {
                this.addNewRowByPosition(e, "add-line");
              },
            },
            {
              name: "copy",
              visible: this.baseView.mode !== "review",
              icon: "copy",
              onClick: (e) => {
                this.addNewRowByPosition(e, "copy-line");
              },
            },
            {
              name: "delete",
              visible: this.baseView.mode !== "review",
            },
          ],
        },
      ];
    },
    backButtonRoute() {
      if (this.baseView.query && this.baseView.query.from) {
        return this.baseView.query.from;
      } else return "/journals";
    },
    getFooterItems() {
      return [
        {
          dataField: "Amount",
          screenField: "FXTotal",
          location: "right",
          value: 0,
          label: "Balance",
          // format: "Decimal-16-4C",
        },
      ];
    },
    getGridHeight() {
      if (this.headerFormHeight)
        return (
          this.gridHeight - (this.gridExpand ? 6 : this.headerFormHeight) - 95
        );
      else return this.gridHeight - 95;

      // -95 => Space for footer
    },
    dataGrid() {
      return this.$refs["dx-data-grid-comp"].instance;
    },
  },
  methods: {
    async returnImportFile(data) {
      this.showLoading(true);
      let tempData = undefined;

      if (
        !this.dataSourceJournalEntry._array[
          this.dataSourceJournalEntry._array.length - 1
        ].GLChartCode
      ) {
        tempData =
          this.dataSourceJournalEntry._array[
            this.dataSourceJournalEntry._array.length - 1
          ];
        this.dataSourceJournalEntry.remove(tempData.ID);
      }

      let inValidFile = true;

      // Create an array of promises for each insertion
      const insertPromises = data.map((element) => {
        // Check if element contains required fields, skip if not
        const hasValidData =
          ((element["GL Account"] || element["GLAccount"]) &&
            element["Branch"]) ||
          (element["Division"] &&
            (element["Line Amount"] || element["LineAmount"]));
        if (!hasValidData) return null;
        else inValidFile = false;

        let obj = {
          GLChartCode: element["GL Account"] || element["GLAccount"],
          GLChartDescription: "",
          BranchCode: element["Branch"]
            ? element["Branch"].toString()
            : element["Branch"],
          DivisionCode: element["Division"]
            ? element["Division"].toString()
            : element["Division"],
          Comment: element["Description"],
          Amount:
            element["Line Amount"] || element["LineAmount"]
              ? element["Line Amount"] || element["LineAmount"]
              : 0,
          IsValid: true,
        };

        if (obj.Amount === 0) return null;

        let branchValid = this.branchDataSourceItems.findIndex(
          (b) => b.Code === obj.BranchCode
        );
        if (branchValid === -1) {
          obj.IsValid = false;
          obj.BranchCode = undefined;
        }
        let divisionValid = this.divisionDataSourceItems.findIndex(
          (d) => d.Code === obj.DivisionCode
        );
        if (divisionValid === -1) {
          obj.IsValid = false;
          obj.DivisionCode = undefined;
        }

        let accountValid = this.glaccountsDataItems.findIndex(
          (a) => a.Code === obj.GLChartCode
        );
        if (accountValid === -1) {
          obj.IsValid = false;
          obj.GLChartCode = undefined;
        } else {
          obj.GLChartDescription =
            this.glaccountsDataItems[accountValid].Description;
        }

        // Insert and return the promise
        return this.dataSourceJournalEntry.insert(obj).then(() => {
          this.dataGrid.refresh(true);
        });
      });

      // Insert tempData if it exists
      if (tempData) {
        delete tempData.ID;
        insertPromises.push(
          this.dataSourceJournalEntry.insert(tempData).then(() => {
            this.dataGrid.refresh(true);
          })
        );
      }

      if (inValidFile) {
        this.showLoading(false);
        await this.$alert("File can not be imported", "Invalid Data");
        return false;
      }

      // Wait for all insertions to complete
      Promise.all(insertPromises)
        .then(() => {
          setTimeout(() => {
            this.setFooterItems(this.dataSourceJournalEntry._array);
            this.showLoading(false);
          }, 1000);
        })
        .catch((error) => {
          if (error) this.showLoading(false); // Make sure loading is hidden even if there's an error
        });
    },
    async setBranchDataSourceItems() {
      let branchDS = BranchService.dataSourceConfig("api/Branch", {
        filter: [["Inactive", "=", "N"]],
        sort: [{ selector: "Code", asc: true }],
        preventPagination: true,
      });

      this.branchDataSourceItems = await branchDS.load();
    },
    async setDivisionDataSourceItems() {
      let divisionDS = DivisionService.dataSourceConfig("api/Division", {
        filter: [["Inactive", "=", "N"]],
        sort: [{ selector: "Code", asc: true }],
        preventPagination: true,
      });

      this.divisionDataSourceItems = await divisionDS.load();
    },
    onCopyClickHandle() {
      this.$router
        .push({
          path: `/journals/create/${this.baseView.id}`,
          query: {
            copy: true,
          },
        })
        .then(() => {
          this.showLoading(true);
          this.refreshView();
          this.loaded = false;
          this.initialized();
        });
    },
    /**
     *
     * @param e
     */
    commentFieldHeightUpdate(e) {
      let instance = e.component;
      let count = countLineBreaks(instance.instance().option().value);
      if (count < 7) {
        if (count > 2) {
          instance.instance().beginUpdate();
          instance.instance().option("height", count * 16);
          instance.instance().endUpdate();
          this.dataGrid.updateDimensions();
        }
      } else {
        instance.instance().beginUpdate();
        instance.instance().option("height", 250);
        instance.instance().endUpdate();
        this.dataGrid.updateDimensions();
      }
    },
    /**
     *
     * @param instance
     * @param arrayData
     * @param idKey
     * @param currentID
     * @returns {*}
     */
    getNextIndex(instance, arrayData, idKey, currentID) {
      const currentIndex = arrayData.findIndex(
        (item) => item[idKey] === currentID
      );
      if (currentIndex !== -1) {
        const nextData = arrayData[currentIndex + 1];
        if (nextData) {
          return instance.getRowIndexByKey(nextData[idKey]);
        }
      }
    },
    /**
     *
     * @param value
     */
    setTab(value = true) {
      this.onTab = value;
    },
    /**
     *
     * @param id
     */
    setLastTabID(id) {
      this.lastTabID = id;
    },
    /**
     *
     * @param e
     */
    setSelectedAccount(e) {
      this.selectedAccount = e.selectedItem;
    },

    setSelectedBranch(e) {
      this.selectedBranch = e.selectedItem;
    },

    setSelectedDivision(e) {
      this.selectedDivision = e.selectedItem;
    },
    /**
     * back button function
     */
    backButtonFunction() {
      this.$router
        .push({
          path: this.backButtonRoute,
        })
        .then(() => {
          // this.$store.dispatch("dataContext/setDataViewType", undefined);
        });
    },
    /**
     *  save journal function
     * @returns {Promise<boolean>}
     */
    async saveJournal() {
      const GridItems = this.dataSourceJournalEntry._array.filter(
        (item) => item.GLChartCode
      );

      let invalidID = {
        amount: null,
        account: null,
        branch: null,
        division: null,
      };

      for (const item of GridItems) {
        if (item.Amount === 0 && invalidID.amount === null) {
          invalidID.amount = item.ID;
        }

        if (
          (!item.GLChartCode || !item.GLChartDescription) &&
          invalidID.account === null
        ) {
          invalidID.account = item.ID;
        }

        if (!item.BranchCode && invalidID.branch === null) {
          invalidID.branch = item.ID;
        }

        if (!item.DivisionCode && invalidID.division === null) {
          invalidID.division = item.ID;
        }
      }

      // Determine which field has the first invalid ID and set alerts accordingly
      const alerts = {
        amount: {
          id: invalidID.amount,
          message: `There are 1 or more lines in this Journal with an amount of 0.00. Please review and update the line amounts before attempting to process this journal.`,
          field: "Amount",
        },
        account: {
          id: invalidID.account,
          message: `There are 1 or more lines in this Journal with an invalid account details. Please review and update the line account before attempting to process this journal.`,
          field: "GLChartCode",
        },
        branch: {
          id: invalidID.branch,
          message: `There are 1 or more lines in this Journal with an invalid branch details. Please review and update the line branch before attempting to process this journal.`,
          field: "BranchCode",
        },
        division: {
          id: invalidID.division,
          message: `There are 1 or more lines in this Journal with an invalid division details. Please review and update the line division before attempting to process this journal.`,
          field: "DivisionCode",
        },
      };

      for (const key in alerts) {
        this.dataGrid.option("focusedRowKey", undefined);
        const { id, message, field } = alerts[key];
        if (id !== null) {
          await this.$alert(message, "Warning");
          this.dataGrid.option("focusedRowKey", id);
          // Function to wait for row rendering before focusing
          this.dataGrid.refresh(true).then(() => {
            const focusOnRowWhenAvailable = (retryCount = 10) => {
              this.$nextTick(() => {
                const rowIndex = this.dataGrid.getRowIndexByKey(id);
                if (rowIndex !== -1) {
                  this.focusOnEditableCell(rowIndex, field);
                } else if (retryCount > 0) {
                  // Retry if row is not yet available
                  setTimeout(
                    () => focusOnRowWhenAvailable(retryCount - 1),
                    100
                  );
                }
              });
            };

            // Initiate focus checking
            focusOnRowWhenAvailable();
          });

          return false;
        }
      }

      const totalInCents = GridItems.map((item) =>
        Math.round(item.Amount * 100)
      ).reduce((acc, val) => acc + val, 0);

      const total = totalInCents / 100;

      if (GridItems.length < 2) {
        await this.$alert(
          `Journal requires at least 2 detail rows.`,
          "Warning"
        );
        return false;
      } else if (total !== 0) {
        await this.$alert(
          `The journal must balance to 0.00. Please check the  amounts before attempting to save again.`,
          "Warning"
        );
        return false;
      } else {
        this.showLoading(true);
        this.loaded = false;
        let JournalDetails = GridItems.map((gridItem) => {
          return {
            BranchCode: gridItem.BranchCode,
            DivisionCode: gridItem.DivisionCode,
            AccountCode: parseInt(gridItem.GLChartCode),
            CompanyCode: this.model.CompanyCode,
            Comment:
              typeof gridItem.Comment === "undefined" ||
              gridItem.Comment === null
                ? ""
                : gridItem.Comment,
            Amount: parseFloat(gridItem.Amount),
          };
        });
        let Journal = {
          AccountingPeriod: this.model.AccountingPeriod,
          // DateIn: this.model.DateIn,
          DateIn: new Date(this.model.DateIn).toSaveDate(),
          Reverse: this.model.Reversed ? "Y" : "N",
          Narration:
            typeof this.model.Narration === "undefined" ||
            this.model.Narration === null
              ? ""
              : this.model.Narration,
          JournalDetails,
        };

        await JournalDetailServices.saveJournalDetail(Journal)
          .then(({ data }) => {
            this.setLoading = false;
            this.$notify(`Journal ${data.Code} saved successfully`, "success");

            this.setIgnoreRouteChangeConfirmation();
            this.$router.push({
              path: `/journals/review/${data.Code}`,
            });

            this.$nextTick(() => {
              this.initialized();
            });
          })
          .catch((err) => {
            this.serverError = {
              err,
              msg: "Journal save failed",
              time: new Date().getTime(),
            };

            this.setLoading = false;
          });
      }
    },
    expandGrid() {
      this.$store.dispatch("ui/setGridExpand", !this.gridExpand);
      setTimeout(() => {
        const dataGrid = this.dataGrid;

        dataGrid.beginUpdate();
        dataGrid.updateDimensions();
        dataGrid.endUpdate();

        dataGrid.refresh(true);
      }, 500);
    },
    /**
     *
     * @param gridItems
     * @returns {Promise<void>}
     */
    async setFooterItems(gridItems) {
      // this.showFooter = false;
      this.getFooterItems
        .filter((x) => (x.hasOwnProperty("visible") ? x.visible : true))
        .forEach((x) => {
          x.value = this.updateValue(x.dataField, gridItems);
          if (x.customCalc) x.value = x.customCalc(this.model, gridItems);
        });
      this.footerItems = this.getFooterItems;
      this.showFooter = true;
    },
    /**
     * initialized
     * @returns {Promise<void>}
     */
    async initialized() {
      await this.setBranchDataSourceItems();
      await this.setDivisionDataSourceItems();
      let isCopy = false;
      if (this.baseView.query) {
        if (this.baseView.query.copy === "true") {
          isCopy = true;
        }
      }
      this.changes = [];
      this.dataSourceJournalEntry.clear();
      // await this.setBranchDataSourceItems();
      if (this.baseView.mode === "create" && !isCopy) {
        await this.$store.dispatch("screenFactors/getFactors", [65]);
        await this.$store.dispatch("screenFactors/getFactors", [65, 66]);

        this.defaultBranchCode = getFactorByCode(65).Factorc;
        this.defaultDivisionCode = getFactorByCode(66).Factorc;

        await JournalDetailServices.getActiveAccountPeriod().then(
          ({ data }) => {
            this.model.MinDate = data.value[0].DateStart;
            this.model.MaxDate = data.value[0].DateEnd;
            this.model.AccountingPeriod = data.value[0].AccountingPeriod;
          }
        );
        await DefaultCompanyBranchDivisionService.getDefault().then(
          ({ data }) => {
            this.model.CompanyCode = data.value[0].CompanyCode;
          }
        );
        for (let index = 0; index < 1; index++) {
          let dataObj = Object.assign(journalBase);

          dataObj.ID = index + 1;
          dataObj.IsValid = true;
          dataObj.BranchCode = this.defaultBranchCode;
          dataObj.DivisionCode = this.defaultDivisionCode;

          this.dataSourceJournalEntry.insert({ ...dataObj });
        }

        // if (getDefaultUserSettings().BranchCode) {
        //   this.defaultDataModel.DefaultBranchCode = this.model.BranchCode =
        //       getDefaultUserSettings().BranchCode;
        // } else {
        //   await BranchService.getDefault().then((res) => {
        //     this.defaultDataModel.DefaultBranchCode = this.model.BranchCode =
        //         res.data.value[0].Code;
        //     this.defaultDataModel.DefaultBranchDescription =
        //         res.data.value[0].Description;
        //   });
        // }
        this.reload = false;
        this.loaded = true;
      } else {
        const journalResult = await JournalDetailServices.getJournal(
          this.baseView.id
        );

        if (journalResult.data.value.length > 0) {
          const journal = journalResult.data.value[0];
          this.model.DateIn = isCopy ? new Date() : journal.DateIn;
          this.model.Reversed = journal.Reversed === "Y";
          this.model.Status = isCopy ? "D" : journal.Status;
          this.model.Narration = journal.Narration;
          this.model.DateCreated = isCopy ? new Date() : journal.DateInto;
          this.model.CreatedBy = isCopy ? null : journal.RaisedBy;
        }

        if (this.baseView.mode === "create" && isCopy) {
          await JournalDetailServices.getActiveAccountPeriod().then(
            ({ data }) => {
              this.model.MinDate = data.value[0].DateStart;
              this.model.MaxDate = data.value[0].DateEnd;
              this.model.AccountingPeriod = data.value[0].AccountingPeriod;
            }
          );
          await DefaultCompanyBranchDivisionService.getDefault().then(
            ({ data }) => {
              this.model.CompanyCode = data.value[0].CompanyCode;
            }
          );
        }

        const journalDetail = await JournalDetailServices.getJournalDetail(
          this.baseView.id
        );
        if (journalDetail.data.value.length > 0) {
          let maxID = 0;
          journalDetail.data.value.forEach((item) => {
            // {
            //   "AccountCode": 1120,
            //     "AccountDescription": "Hulling / Shelling Revenue",
            //     "Amount": 1000,
            //     "BranchCode": "01",
            //     "BranchDescription": "RENMARK",
            //     "Comment": "Test Comment",
            //     "DivisionCode": "01",
            //     "DivisionDescription": "Almondco Company",
            //     "JournalCode": 61092,
            //     "LineNo": 1,
            //     "CompanyCode": "01"
            // }
            const dataObj = Object.assign({ ...journalBase });
            dataObj.ID = ++maxID;

            dataObj.GLChartCode = item.AccountCode;
            dataObj.ComputedStringCode = item.AccountCode;
            dataObj.GLChartDescription = item.AccountDescription;
            dataObj.BranchCode = item.BranchCode;
            dataObj.CompanyCode = item.CompanyCode;
            dataObj.BranchDescription = item.BranchDescription;
            dataObj.DivisionCode = item.DivisionCode;
            dataObj.DivisionDescription = item.DivisionDescription;
            dataObj.Comment = item.Comment;
            dataObj.Amount = item.Amount;
            dataObj.AmountString = item.Amount;
            this.dataSourceJournalEntry.insert(dataObj);
          });
          // const dataGrid = this.dataGrid;
          // dataGrid.saveEditData();
          // dataGrid.refresh();
        }

        this.ellipsisMenu = [
          {
            menuText: "Copy",
            visible: this.baseView.mode == "review",
            disabled: FeatureStorage.checkPermission("Journals", "create")
              ? false
              : true,
          },
        ];

        this.loaded = true;
        // console.log(journalData)
        // setTimeout(() => {
        //   this.reload = false;
        //   this.loaded = true;
        //   this.setLoading = false;
        // }, 250);
      }

      this.showLoading(false);
      this.expandChildGrid = true;
    },
    /**
     *
     * @param dataField
     * @param items
     * @returns {number}
     */
    updateValue(dataField, items) {
      let result = 0;
      if (items.length > 0)
        items.forEach((x) => {
          if (x.CheckInclude || this.baseView.mode === "review")
            result += parseFloat(x[dataField]).roundNumber(4);
          else if (x.CheckInclude === undefined)
            result += parseFloat(x[dataField]).roundNumber(4);
        });

      return result;
    },
    /**
     * form content ready
     * @param e
     */
    formContentReady(e) {
      const headerObject = document.querySelector(
        ".dx-item-content.dx-box-item-content.dx-box-flex.dx-box.dx-widget.dx-visibility-change-handler.dx-collection"
      );
      headerObject.children[0].style = "";
      headerObject.children[0].style = "width: 300px";
      const headerForm = document.querySelectorAll(".gc-header-form");
      this.headerFormHeight = headerForm[0].clientHeight;
      if (!this.isFirstLoad) return false;
      this.isFirstLoad = false;
      if (e && this.baseView.mode !== "review" && this.loaded) {
        setTimeout(() => {
          const headerToolBarForm = e.component;
          if (headerToolBarForm && headerToolBarForm.getEditor("DateIn"))
            headerToolBarForm.getEditor("DateIn").instance().focus();
        }, 50);
      }

      // this.headerDxFormHeight = e.element.offsetHeight;
      // setTimeout(() => {
      //   var BranchEditor = e.component.getEditor("BranchCode");
      //   if (BranchEditor) BranchEditor.focus();
      // }, 100);
    },
    /**
     * Toolbar preparing
     */
    onToolbarPreparing(e) {
      var toolbarItems = e.toolbarOptions.items;
      var searchPanel = toolbarItems.find((item) => {
        return item.name === "searchPanel";
      });

      e.toolbarOptions.items = toolbarItems.filter((item) => {
        return item.name !== "searchPanel";
      });

      searchPanel.location = "after";

      e.toolbarOptions.items.unshift(
        {
          location: "before",
          widget: "dxButton",
          options: {
            // icon: "plus",
            text: "Import",
            onClick: async () => {
              this.visibleFileUploader = !this.visibleFileUploader;
              this.getGLAccounts();
            },
            visible: this.baseView.mode != "review",
          },
        },
        searchPanel
      );
    },
    async getGLAccounts() {
      if (this.glaccountsDataItems.length === 0) {
        let glAccounts = ProductService.dataSourceConfig("api/GLChart", {
          filter: [["Inactive", "=", "N"], "and", ["Status", "=", "A"]],
          select: ["Code", "ComputedStringCode", "Description"],
          sort: "ComputedStringCode asc",
          preventPagination: true,
        });

        this.glaccountsDataItems = await glAccounts.load();
      }
    },
    /**
     * validation comeback
     * @param column
     * @param key
     */
    setValidationComeBack(column, key) {
      this.validationColumn = column;
      this.validationDataRowKey = key;
    },
    /**
     * editor preparing
     * @param e
     */
    onEditorPreparing(e) {
      if (e.editorOptions && e.editorOptions.onFocusOut) {
        let func = e.editorOptions.onFocusOut;
        if (this.baseView.mode !== "review" && e.parentType !== "searchPanel")
          e.editorOptions.onFocusOut = (event) => {
            func(event, {
              data: e.row.data,
              component: e.component,
              index: e.index,
              rowIndex: e.row.rowIndex,
            });
          };
      }
      if (e.editorOptions && e.editorOptions.onKeyDown) {
        let func = e.editorOptions.onKeyDown;
        if (this.baseView.mode !== "review" && e.parentType !== "searchPanel")
          e.editorOptions.onKeyDown = (event) => {
            // if (onEnter(event) || forwardTabbing(event)) {
            //   this.goToNextEditableComponent(e.row, e.name);
            //   event.event.preventDefault();
            // } else if (backwardTabbing(event)) {
            //   event.event.preventDefault();
            //   this.goToPrevEditableComponent(e.row, e.name);
            // }
            func(event, {
              column: {
                dataField: e.dataField,
              },
              data: e.row.data,
              index: e.index,
              rowIndex: e.row.rowIndex,
            });
          };
      }
      //
      // if (e.editorOptions && e.editorOptions.onItemClick) {
      //   let func = e.editorOptions.onItemClick;
      //   if (this.baseView.mode !== "review" && e.parentType !== "searchPanel")
      //     e.editorOptions.onItemClick = (event) => {
      //       func(event, {
      //         column: {
      //           dataField: e.dataField,
      //         },
      //         data: e.row.data,
      //         rowIndex: e.row.rowIndex,
      //       });
      //     };
      // }

      if (
        e.dataField &&
        (e.dataField === "GLChartCode" ||
          e.dataField === "BranchCode" ||
          e.dataField === "DivisionCode")
      ) {
        e.editorName = "dxSelectBox";
      }

      if (e.dataField && e.dataField === "Comment") {
        e.editorName = "dxTextArea";
      }

      // if (e.rowType === "data") {
      //   // if (e.column.dataField === "TransferQty") {
      //   //   if (e.data.TransferQty > e.data.Onhand) {
      //   //     e.cellElement.classList.add("gc-require-validation");
      //   //   }
      //   // }
      // }
    },
    /**
     * Set all empty comment in table item
     */
    setAllEmptyCommentsItem() {
      const emptyComments = this.dataSourceJournalEntry._array.filter(
        (item) =>
          item.Comment === "" ||
          item.Comment === null ||
          typeof item.Comment === "undefined"
      );

      emptyComments.map((item) => (item.Comment = this.model.Narration));
      this.dataGrid.refresh();
    },
    /**
     * Go To Comment Form
     * @param editor
     */
    goToCommentForm(editor = "Narration") {
      const headerCommentsForm =
        this.$refs["gc-header-form"].$refs["gc-form-comp"].instance;
      if (headerCommentsForm) {
        headerCommentsForm.getEditor(editor).focus();
      }
    },
    /**
     * Focus On Editable Cell
     * @param rowIndex
     * @param column
     */
    focusOnEditableCell(rowIndex, column = "GLChartCode") {
      const dataGrid = this.dataGrid;

      const cellElement = dataGrid.getCellElement(rowIndex, column);
      dataGrid.focus(cellElement);

      setTimeout(() => {
        dataGrid.editCell(rowIndex, column);
      }, 10);
    },
    /**
     * add New Row
     * @param isFocus
     */
    addNewRow(isFocus = true) {
      let maxID = 0;

      maxID = Math.max(...this.dataSourceJournalEntry._array.map((e) => e.ID));

      if (this.dataSourceJournalEntry._array.length === 0) maxID = 0;
      let dataObj = Object.assign({ ...journalBase });
      dataObj.ID = ++maxID;
      if (this.model.isDefaultComment) {
        dataObj.Comment = this.model.Narration;
      }
      this.dataSourceJournalEntry.insert(dataObj);
      const dataGrid = this.dataGrid;
      dataGrid.saveEditData();
      dataGrid.refresh();
      if (isFocus) {
        setTimeout(() => {
          const rowIndex = this.dataGrid.getRowIndexByKey(dataObj.ID);
          if (rowIndex !== -1) {
            this.focusOnEditableCell(rowIndex);
          }
        }, 50);
      }
    },
    /**
     *
     * @param type
     * @param dataObj
     * @returns {Promise<void>}
     */
    async updateLocalDataSet(type, dataObj) {
      if (type === "insert") {
        if (
          this.dataSourceJournalEntry._array.length !== 0 &&
          this.dataSourceJournalEntry._array[
            this.dataSourceJournalEntry._array.length - 1
          ].GLChartCode
        ) {
          this.dataSourceJournalEntry.insert(dataObj);
        } else if (this.dataSourceJournalEntry._array.length === 0) {
          this.dataSourceJournalEntry.insert(dataObj);
        }
      } else if (type === "update") {
        this.dataSourceJournalEntry.update(dataObj.ID, dataObj);
      }
      const dataGrid = this.dataGrid;
      dataGrid.saveEditData();
      dataGrid.refresh();
    },
    /**
     *
     * @param e
     * @param type
     * @returns {Promise<void>}
     */
    async addNewRowByPosition(e, type) {
      const key = this.dataGrid.getRowIndexByKey(e.row.data.ID);
      if (
        e.row.data.GLChartCode === null ||
        e.row.data.GLChartCode === "" ||
        typeof e.row.data.GLChartCode === "undefined"
      ) {
        this.setValidationComeBack("GLChartCode", key);
        this.displayValidationErrors([
          "The account provided is not a valid account for the journal. Please select a valid account.",
        ]);
      }
      // else if (e.row.data.Amount === 0) {
      //   this.setValidationComeBack("Amount", key);
      //   this.displayValidationErrors([
      //     `The amount provided is not a valid amount for the journal. Please fill in a valid amount.`,
      //   ]);
      // }
      else {
        let dataObj = undefined;
        let maxID = Math.max(
          ...this.dataSourceJournalEntry._array.map((e) => e.ID)
        );

        if (type === "add-line") {
          dataObj = Object.assign({ IsNewLine: true }, { ...journalBase });
        } else {
          dataObj = Object.assign({ ...e.row.data });
        }
        dataObj.ID = ++maxID;
        if (this.model.isDefaultComment && type === "add-line") {
          dataObj.Comment = this.model.Narration;
        }

        this.changes = [
          {
            type: "insert",
            data: dataObj,
            insertAfterKey: e.row.key,
          },
        ];

        const dataGrid = this.dataGrid;
        dataGrid.clearSelection();
        dataGrid.option("focusedRowIndex", -1);

        setTimeout(() => {
          dataGrid.saveEditData();
          dataGrid.refresh(true);
        }, 250);

        setTimeout(() => {
          const rowIndex = this.dataGrid.getRowIndexByKey(dataObj.ID);
          if (rowIndex !== -1) {
            this.focusOnEditableCell(rowIndex);
          }
        }, 600);
      }
    },
    /**
     * Show Alert
     * @param index
     * @returns {Promise<void>}
     */
    async showGlCodeAlert(index) {
      await this.$alert(
        `The account provided is not a valid account for the journal. Please select a valid Account.`,
        "Warning"
      );
      this.focusOnEditableCell(index, "GLChartCode");
    },
    /**
     *
     * @param e
     */
    onRowPrepared(e) {
      if (e.rowType === "data") {
        if (!e.data.IsValid && this.baseView.mode !== "review") {
          e.rowElement.classList.add("alert-row");
          if (e.rowElement.classList.contains("dx-row-alt")) {
            e.rowElement.classList.add("dx-row-alt-back");
            e.rowElement.classList.remove("dx-row-alt");
          }
        } else {
          e.rowElement.classList.remove("alert-row");
          if (e.rowElement.classList.contains("dx-row-alt-back")) {
            e.rowElement.classList.remove("dx-row-alt-back");
            e.rowElement.classList.add("dx-row-alt");
          }
        }
      }
    },
    /**
     *
     * @param e
     */
    onCellPrepared(e) {
      if (e.rowType === "data" && e.value < 0) {
        let cell = e.cellElement;
        cell.classList.add("negative-decimal");
        cell.setAttribute("id", "negative-decimal");
      }
    },
    /**
     *
     * @param e
     */
    onRowRemoved(e) {
      if (this.dataSourceJournalEntry._array.length === 0) {
        this.addNewRow();
        e.component.refresh();
      } else {
        const lineData =
          this.dataSourceJournalEntry._array[
            this.dataSourceJournalEntry._array.length - 1
          ];
        if (lineData) {
          const batchKey = this.dataGrid.getRowIndexByKey(lineData.ID);
          if (batchKey !== -1) {
            this.focusOnEditableCell(batchKey);
          }
        }
      }
    },
  },
  async mounted() {},
  /**
   * Before Created
   */
  beforeCreate() {
    this.$store.dispatch("defaultUserSettings/setDefaultCompanyBranchDivision");
  },
  /**
   * On Created
   * @returns {Promise<void>}
   */
  async created() {
    this.showLoading(true);

    // this.journalItems.push({
    //   GLChartCode: undefined,
    //   GLChartDescription: undefined,
    //   BranchCode: this.defaultBranchCode,
    //   BranchDescription: undefined,
    //   DivisionCode: this.defaultDivisionCode,
    //   DivisionDescription: undefined,
    //   Comment: undefined,
    //   Amount: 0,
    // });
    await this.initialized();
  },
  watch: {
    async getValidationSummaryVisible(val) {
      if (!val && this.validationColumn) {
        this.focusOnEditableCell(
          this.validationDataRowKey,
          this.validationColumn
        );
        this.validationColumn = undefined;
        this.validationDataRowKey = undefined;
      }
    },
    dataSourceJournalEntry: {
      handler(val) {
        this.setFooterItems(val._array);
      },
      deep: true,
    },
  },
};
</script>
<style lang="scss">
.class-multiple {
  .dx-selectbox-container .dx-texteditor-container .dx-placeholder {
    color: black;
  }
}

.gc-state-negative .dx-texteditor-input {
  color: red;
}

.alert-row {
  background-color: #ffcccc; /* Light red background */
  color: #a94442; /* Dark red text */
}

.journal-v2 {
  .dx-button[aria-label="Import"] {
    background-color: #03a9f4;
    color: #fff !important;

    span {
      padding-right: 5px;
      padding-left: 5px;
    }

    .dx-button-content .dx-icon::before {
      color: #fff !important;
    }
  }
  .edit-cell-datebox-field-border .dx-datebox::after {
    content: "";
    border-bottom: 1px solid rgba(0, 0, 0, 0.42);
  }

  .header-form {
    margin-bottom: unset !important;
  }

  //.header-form-container {
  //  width: 100%;
  //  display: inline-flex;
  //
  //  .header-form {
  //    width: calc((100% / 3) * 2);
  //    padding-right: 5px;
  //    margin-bottom: 0 !important;
  //  }
  //
  //  .header-comments {
  //    width: calc((100% / 3) * 1);
  //    padding-left: 5px;
  //  }
  //}

  .gc-require-validation::after,
  .gc-require-validation .dx-texteditor::after,
  .gc-require-validation.edit-cell-numberbox-field
    .dx-numberbox.dx-texteditor:not(.dx-state-readonly)::after {
    content: "";
    border-bottom: 2px solid rgb(255 0 0) !important;
  }

  //td.gc-require-validation:not(.dx-editor-cell) {
  //  background-color: rgba(255, 0, 0, 0.259) !important;
  //}
  //
  //.dx-datagrid-rowsview
  //  .dx-row-focused.dx-data-row
  //  > td.gc-require-validation:not(.dx-focused) {
  //  background-color: rgba(255, 0, 0, 0.259) !important;
  //}

  .dx-datagrid {
    .comment-text .dx-texteditor-container .dx-texteditor-input-container {
      textarea {
        line-height: unset !important;
        height: inherit !important;
        overflow: auto;
      }
    }

    .comment-cell-template {
      max-height: 103px !important;
      overflow: auto;
    }
  }

  .dx-datagrid
    .dx-datagrid-content
    .dx-datagrid-table
    .dx-row
    > td:not(.dx-validation-pending):not(.dx-datagrid-select-all) {
    vertical-align: top !important;
  }
}

.journal-screen-container {
  border: 10px solid rgba(225, 225, 233, 0.7803921569);
  background: white;
}

#journal-screen {
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;

  .css-v2.transaction-screen {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }

  .css-v2.transaction-screen .gc-header-form {
    padding: 0 0 10px 0;
    // height: 140px;
  }

  .dx-datagrid .dx-datagrid-content .dx-datagrid-table .dx-row .dx-editor-cell {
    padding-left: 5px !important;
    padding-right: 5px !important;
  }
}
</style>
